import { useCallback, useEffect } from "react";

import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router-dom";

import useAuth from "@app/common/auth/useAuth";
import useApps from "@app/common/hooks/useApps";
import { showToast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import { MeetingPlatform } from "@app/common/types";
import { withQueryParams } from "@app/common/utils";

const getSignInRoute = (client: MeetingPlatform) => {
  if (client === "meet") {
    return withQueryParams(
      ROUTES.MEETING_TOOLS_HOME,
      undefined,
      window.location.search
    );
  }

  return withQueryParams(ROUTES.SIGN_IN, {
    redirectTo: `${window.location.pathname}?${window.location.search}`,
  });
};

const useRefreshTokenFailed = (
  { enabled }: { enabled: boolean } = { enabled: true }
) => {
  const { client } = useApps();
  const navigate = useNavigate();
  const { user, wipeUserState } = useAuth();

  const redirectToSignIn = useCallback(async () => {
    if (!user) return;

    wipeUserState(false);
    if (client === "browser" || client === "meet") {
      showToast({
        message: "Your session has expired. Please sign in again.",
        severity: "warning",
      });
      const signInRoute = getSignInRoute(client);
      navigate(signInRoute, { replace: true });
    } else {
      Sentry.captureMessage("refresh_token_failed event fired outside browser");
      window.location.reload();
    }
  }, [client, Boolean(user)]);

  useEffect(() => {
    if (enabled) {
      window.addEventListener("refresh_token_failed", redirectToSignIn);
    } else {
      window.removeEventListener("refresh_token_failed", redirectToSignIn);
    }

    return () => {
      window.removeEventListener("refresh_token_failed", redirectToSignIn);
    };
  }, [redirectToSignIn, enabled]);
};

export default useRefreshTokenFailed;
